import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { QuestionBlock, SingleSelect, MultiSelect, CurrencyField, formatRupiah } from "@/components/AssessmentFields";
import {
  EMPTY_ANSWERS, SECTIONS, TOTAL_STEPS, validateStep, deployableCapital, findMissingRequired,
  OPERATING_BASE_OPTIONS, INTEREST_OPTIONS, SKILL_OPTIONS, EXPERIENCE_OPTIONS,
  TIME_OPTIONS, TEAM_OPTIONS, ASSET_OPTIONS, GOAL_OPTIONS, INCOME_OPTIONS,
  MODE_OPTIONS, PRIORITY_OPTIONS, RISK_OPTIONS,
} from "@/lib/assessmentConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ArrowLeft, ArrowRight, ClipboardList, CheckCircle2, Pencil } from "lucide-react";
import { toast } from "sonner";

const FIELD_KEYS = Object.keys(EMPTY_ANSWERS);

function pickAnswers(data) {
  const out = { ...EMPTY_ANSWERS };
  FIELD_KEYS.forEach((k) => {
    if (data[k] !== undefined && data[k] !== null) out[k] = data[k];
  });
  return out;
}

export default function Assessment() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isEdit = params.get("edit") === "1";

  const [phase, setPhase] = useState("intro"); // intro | wizard | review
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState(EMPTY_ANSWERS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [editStep, setEditStep] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assessment");
        if (data) {
          setAnswers(pickAnswers(data));
          const done = data.assessment_status === "completed";
          setCompleted(done);
          if (isEdit) setPhase("wizard");
          else if (done) setPhase("review");
          else setPhase("wizard");
        }
      } catch {
        /* ignore, start fresh */
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit]);

  const set = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  const persist = async (status) => {
    const { data } = await api.put("/assessment", { ...answers, status });
    if (data) setCompleted(data.assessment_status === "completed");
    return data;
  };

  const goNext = async () => {
    const err = validateStep(step, answers);
    if (err) { setError(err); return; }
    setError("");
    setSaving(true);
    try {
      await persist("draft");
      if (step < TOTAL_STEPS) setStep(step + 1);
      else setPhase("review");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    setError("");
    if (step > 1) { setStep(step - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else setPhase("intro");
  };

  const confirmProfile = async () => {
    setError("");
    // 1) Client-side required-field check (names + jump target), so the user
    //    never sees a generic "incomplete" error and knows exactly what to fix.
    const missing = findMissingRequired(answers);
    if (missing.length > 0) {
      setError("Beberapa jawaban wajib belum lengkap: " + missing.map((m) => m.label).join(", ") + ".");
      setEditStep(missing[0].step);
      toast.error("Lengkapi dulu: " + missing.map((m) => m.label).join(", "));
      return;
    }
    // 2) Capital rule: deployable = available - reserve, must be >= 0.
    if (Number(answers.capital_reserve) > Number(answers.available_capital)) {
      setError("Dana cadangan tidak boleh melebihi total modal.");
      setEditStep(2);
      return;
    }
    setSaving(true);
    try {
      await persist("completed");
      toast.success("Profil bisnis Anda tersimpan!");
      navigate("/dashboard");
    } catch (e) {
      // Surface the actual server detail (which fields), not a generic message.
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-brand-accent" />
        </div>
      </DashboardLayout>
    );
  }

  // ---------------- INTRO ----------------
  if (phase === "intro") {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl animate-fade-up" data-testid="assessment-intro">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand/10 text-brand">
            <ClipboardList className="h-7 w-7" />
          </div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">
            Kenali Kondisi Anda Dulu
          </h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            IDEbisnis AI menggunakan kondisi nyata Anda untuk menyaring peluang bisnis yang lebih masuk akal.
            Jawab berdasarkan kondisi Anda sekarang, bukan kondisi ideal yang Anda harapkan.
          </p>
          <div className="mt-6 space-y-2 rounded-xl border border-borderc bg-white p-5 text-sm text-ink-soft">
            <p className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-accent" /> Tidak ada jawaban benar atau salah.</p>
            <p className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-brand-accent" /> Informasi ini digunakan untuk membantu menyesuaikan analisis bisnis Anda.</p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button onClick={() => setPhase("wizard")} data-testid="intro-start" className="h-12 bg-brand hover:bg-brand-hover text-base px-6">
              Mulai <ArrowRight className="ml-1 h-5 w-5" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="intro-back" className="h-12 border-borderc text-base px-6">
              Kembali ke Dashboard
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ---------------- REVIEW ----------------
  if (phase === "review") {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-3xl animate-fade-up" data-testid="assessment-review">
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Ringkasan Kondisi Anda</h1>
          <p className="mt-3 text-base text-ink-soft leading-relaxed">
            Pastikan informasi ini sesuai kondisi Anda sekarang karena akan digunakan untuk menyaring dan membandingkan peluang bisnis.
          </p>

          {error && <div className="mt-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="review-error">{error}</div>}

          <div className="mt-6 space-y-4">
            <ReviewCard title="Lokasi & Operasi" rows={[
              ["Lokasi", answers.business_location || "-"],
              ["Cara Beroperasi", answers.operating_base || "-"],
            ]} />
            <ReviewCard title="Modal" highlight rows={[
              ["Total Modal", formatRupiah(answers.available_capital) || "-"],
              ["Dana Cadangan", formatRupiah(answers.capital_reserve) || "-"],
              ["Modal yang Dapat Digunakan", formatRupiah(deployableCapital(answers.available_capital, answers.capital_reserve))],
            ]} />
            <ReviewCard title="Minat & Kemampuan" rows={[
              ["Bidang Minat", listVal(answers.business_interests, answers.business_interests_other)],
              ["Kemampuan", listVal(answers.skills, answers.skills_other)],
              ["Pengalaman", answers.business_experience || "-"],
              ...(answers.business_experience_note ? [["Catatan", answers.business_experience_note]] : []),
            ]} />
            <ReviewCard title="Waktu & Tim" rows={[
              ["Ketersediaan Waktu", answers.available_time || "-"],
              ["Tim", answers.team_structure || "-"],
              ...(answers.team_structure_note ? [["Catatan Tim", answers.team_structure_note]] : []),
            ]} />
            <ReviewCard title="Aset" rows={[
              ["Aset Tersedia", listVal(answers.available_assets, answers.available_assets_other)],
            ]} />
            <ReviewCard title="Tujuan" rows={[
              ["Tujuan Utama", answers.primary_goal === "Lainnya" && answers.primary_goal_other ? answers.primary_goal_other : (answers.primary_goal || "-")],
              ["Target Penghasilan", answers.income_target || "-"],
            ]} />
            <ReviewCard title="Preferensi" rows={[
              ["Model Bisnis", answers.business_mode || "-"],
              ["Prioritas", answers.business_priorities.join(", ") || "-"],
              ["Toleransi Risiko", answers.risk_tolerance || "-"],
            ]} />
            <ReviewCard title="Batasan" rows={[
              ["Catatan Tambahan", answers.additional_constraints || "-"],
            ]} />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button onClick={confirmProfile} disabled={saving} data-testid="confirm-profile" className="h-12 bg-brand hover:bg-brand-hover text-base px-6">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Konfirmasi Profil <CheckCircle2 className="ml-1 h-5 w-5" /></>}
            </Button>
            <Button variant="outline" onClick={() => { setPhase("wizard"); setStep(editStep); setError(""); }} data-testid="edit-answers" className="h-12 border-borderc text-base px-6">
              <Pencil className="mr-1 h-4 w-4" /> Edit Jawaban
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ---------------- WIZARD ----------------
  const section = SECTIONS[step - 1];
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl" data-testid="assessment-wizard">
        {/* progress */}
        <div className="sticky top-16 z-20 -mx-4 sm:mx-0 bg-surface/95 backdrop-blur px-4 sm:px-0 py-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-brand" data-testid="wizard-progress">Langkah {step} dari {TOTAL_STEPS}</span>
            <span className="text-ink-muted">{section.title}</span>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
          </div>
        </div>

        {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="wizard-error">{error}</div>}

        <div className="mt-6 space-y-8 animate-fade-up pb-28 sm:pb-2" key={step}>
          {step === 1 && <StepLocation answers={answers} set={set} />}
          {step === 2 && <StepCapital answers={answers} set={set} />}
          {step === 3 && <StepInterests answers={answers} set={set} />}
          {step === 4 && <StepTime answers={answers} set={set} />}
          {step === 5 && <StepAssets answers={answers} set={set} />}
          {step === 6 && <StepPreferences answers={answers} set={set} />}
        </div>

        <div className="sticky bottom-0 z-30 -mx-4 mt-10 flex gap-3 border-t border-borderc bg-white/95 px-4 py-4 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-none">
          <Button variant="outline" onClick={goBack} data-testid="wizard-back" className="h-12 flex-1 sm:flex-none border-borderc px-6">
            <ArrowLeft className="mr-1 h-5 w-5" /> Kembali
          </Button>
          <Button onClick={goNext} disabled={saving} data-testid="wizard-next" className="h-12 flex-1 sm:flex-none bg-brand hover:bg-brand-hover px-8">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (step < TOTAL_STEPS ? <>Lanjut <ArrowRight className="ml-1 h-5 w-5" /></> : <>Tinjau <ArrowRight className="ml-1 h-5 w-5" /></>)}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function listVal(arr, other) {
  const items = [...(arr || [])];
  if (other && items.includes("Lainnya")) {
    return items.map((i) => (i === "Lainnya" ? `Lainnya: ${other}` : i)).join(", ");
  }
  return items.join(", ") || "-";
}

function ReviewCard({ title, rows, highlight }) {
  return (
    <div className="rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid={`review-card-${title.replace(/\s|&/g, "").toLowerCase()}`}>
      <h3 className="font-heading text-xs font-bold uppercase tracking-wide text-brand-accent">{title}</h3>
      <dl className="mt-4 space-y-3">
        {rows.map(([k, v], i) => (
          <div key={i} className={`flex flex-col sm:flex-row sm:justify-between gap-1 ${highlight && i === rows.length - 1 ? "rounded-lg bg-brand/5 -mx-2 px-2 py-2" : ""}`}>
            <dt className="text-sm text-ink-muted">{k}</dt>
            <dd className={`text-sm sm:text-right sm:max-w-[60%] ${highlight && i === rows.length - 1 ? "font-bold text-brand" : "font-medium text-ink"}`}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ---------- step components ----------
function StepLocation({ answers, set }) {
  return (
    <>
      <QuestionBlock label="Di mana bisnis akan dijalankan?" testid="q-business_location">
        <Input data-testid="business_location" className="h-12 text-base" placeholder="Contoh: Makassar"
          value={answers.business_location} onChange={(e) => set("business_location", e.target.value)} />
      </QuestionBlock>
      <QuestionBlock label="Dari mana Anda berencana menjalankan bisnis?" testid="q-operating_base">
        <SingleSelect name="operating_base" options={OPERATING_BASE_OPTIONS} value={answers.operating_base}
          onChange={(v) => set("operating_base", v)} columns={2} />
      </QuestionBlock>
    </>
  );
}

function StepCapital({ answers, set }) {
  const deployable = deployableCapital(answers.available_capital, answers.capital_reserve);
  return (
    <>
      <p className="rounded-xl border border-borderc bg-white p-4 text-sm text-ink-soft">
        Pisahkan modal yang tersedia dengan uang yang ingin tetap Anda simpan sebagai cadangan.
      </p>
      <QuestionBlock label="Berapa total modal yang saat ini tersedia untuk memulai bisnis?" testid="q-available_capital">
        <CurrencyField name="available_capital" placeholder="5.000.000"
          value={answers.available_capital} onChange={(v) => set("available_capital", v)} />
      </QuestionBlock>
      <QuestionBlock label="Dari modal tersebut, berapa yang ingin tetap Anda simpan sebagai cadangan?" testid="q-capital_reserve">
        <CurrencyField name="capital_reserve" placeholder="0"
          value={answers.capital_reserve} onChange={(v) => set("capital_reserve", v)} />
      </QuestionBlock>
      <div className="rounded-2xl border border-brand-accent bg-brand/5 p-5" data-testid="deployable-display">
        <div className="text-sm text-ink-soft">Modal yang dapat digunakan untuk tahap awal</div>
        <div className="mt-1 font-heading text-2xl font-extrabold text-brand" data-testid="deployable-value">{formatRupiah(deployable)}</div>
        <p className="mt-2 text-xs text-ink-muted leading-relaxed">
          IDEbisnis AI akan menggunakan angka ini sebagai batas utama dalam menyaring peluang bisnis.
        </p>
      </div>
    </>
  );
}

function StepInterests({ answers, set }) {
  return (
    <>
      <QuestionBlock label="Bidang bisnis apa yang paling menarik bagi Anda?" helper="Pilih maksimal 3." testid="q-business_interests">
        <MultiSelect name="business_interests" options={INTEREST_OPTIONS} values={answers.business_interests}
          onChange={(v) => set("business_interests", v)} max={3} />
        {answers.business_interests.includes("Lainnya") && (
          <Input data-testid="business_interests_other" className="mt-2 h-11" placeholder="Sebutkan bidang lainnya"
            value={answers.business_interests_other} onChange={(e) => set("business_interests_other", e.target.value)} />
        )}
      </QuestionBlock>
      <QuestionBlock label="Kemampuan apa yang sudah Anda miliki?" helper="Pilih semua yang sesuai." testid="q-skills">
        <MultiSelect name="skills" options={SKILL_OPTIONS} values={answers.skills} onChange={(v) => set("skills", v)} />
        {answers.skills.includes("Lainnya") && (
          <Input data-testid="skills_other" className="mt-2 h-11" placeholder="Sebutkan kemampuan lainnya"
            value={answers.skills_other} onChange={(e) => set("skills_other", e.target.value)} />
        )}
      </QuestionBlock>
      <QuestionBlock label="Bagaimana pengalaman bisnis Anda?" testid="q-business_experience">
        <SingleSelect name="business_experience" options={EXPERIENCE_OPTIONS} value={answers.business_experience}
          onChange={(v) => set("business_experience", v)} />
        <Textarea data-testid="business_experience_note" className="mt-3" placeholder="Ceritakan singkat jika ada pengalaman yang relevan. (opsional)"
          value={answers.business_experience_note} onChange={(e) => set("business_experience_note", e.target.value)} />
      </QuestionBlock>
    </>
  );
}

function StepTime({ answers, set }) {
  return (
    <>
      <QuestionBlock label="Berapa waktu yang realistis dapat Anda gunakan untuk bisnis?" testid="q-available_time">
        <SingleSelect name="available_time" options={TIME_OPTIONS} value={answers.available_time}
          onChange={(v) => set("available_time", v)} columns={2} />
      </QuestionBlock>
      <QuestionBlock label="Siapa yang akan menjalankan bisnis?" testid="q-team_structure">
        <SingleSelect name="team_structure" options={TEAM_OPTIONS} value={answers.team_structure}
          onChange={(v) => set("team_structure", v)} columns={2} />
        <Textarea data-testid="team_structure_note" className="mt-3" placeholder="Tambahkan informasi jika ada bantuan yang hanya tersedia pada waktu tertentu. (opsional)"
          value={answers.team_structure_note} onChange={(e) => set("team_structure_note", e.target.value)} />
      </QuestionBlock>
    </>
  );
}

function StepAssets({ answers, set }) {
  return (
    <>
      <QuestionBlock label="Aset apa yang sudah dapat digunakan untuk bisnis?" helper="Pilih semua yang sesuai." testid="q-available_assets">
        <MultiSelect name="available_assets" options={ASSET_OPTIONS} values={answers.available_assets}
          onChange={(v) => set("available_assets", v)} />
        {answers.available_assets.includes("Lainnya") && (
          <Input data-testid="available_assets_other" className="mt-2 h-11" placeholder="Sebutkan aset lainnya"
            value={answers.available_assets_other} onChange={(e) => set("available_assets_other", e.target.value)} />
        )}
      </QuestionBlock>
      <QuestionBlock label="Apa tujuan utama Anda memulai bisnis?" testid="q-primary_goal">
        <SingleSelect name="primary_goal" options={GOAL_OPTIONS} value={answers.primary_goal}
          onChange={(v) => set("primary_goal", v)} />
        {answers.primary_goal === "Lainnya" && (
          <Input data-testid="primary_goal_other" className="mt-2 h-11" placeholder="Sebutkan tujuan lainnya"
            value={answers.primary_goal_other} onChange={(e) => set("primary_goal_other", e.target.value)} />
        )}
      </QuestionBlock>
      <QuestionBlock label="Berapa target penghasilan bisnis yang Anda harapkan?"
        helper="Target ini membantu memahami tujuan Anda. Ini bukan proyeksi atau jaminan penghasilan." testid="q-income_target">
        <SingleSelect name="income_target" options={INCOME_OPTIONS} value={answers.income_target}
          onChange={(v) => set("income_target", v)} columns={2} />
      </QuestionBlock>
    </>
  );
}

function StepPreferences({ answers, set }) {
  return (
    <>
      <QuestionBlock label="Model bisnis seperti apa yang Anda inginkan?" testid="q-business_mode">
        <SingleSelect name="business_mode" options={MODE_OPTIONS} value={answers.business_mode}
          onChange={(v) => set("business_mode", v)} columns={2} />
      </QuestionBlock>
      <QuestionBlock label="Apa dua prioritas terpenting bagi Anda?" helper="Pilih maksimal 2." testid="q-business_priorities">
        <MultiSelect name="business_priorities" options={PRIORITY_OPTIONS} values={answers.business_priorities}
          onChange={(v) => set("business_priorities", v)} max={2} />
      </QuestionBlock>
      <QuestionBlock label="Seberapa nyaman Anda mengambil risiko bisnis?" testid="q-risk_tolerance">
        <SingleSelect name="risk_tolerance" options={RISK_OPTIONS} value={answers.risk_tolerance}
          onChange={(v) => set("risk_tolerance", v)} />
      </QuestionBlock>
      <QuestionBlock label="Apakah ada batasan atau kondisi lain yang perlu kami ketahui?"
        helper="Contoh: tidak ingin menyewa tempat, tidak ingin bisnis kuliner, tidak bisa bekerja malam, tidak ingin menyimpan stok, harus bisa dikerjakan dari rumah." testid="q-additional_constraints">
        <Textarea data-testid="additional_constraints" placeholder="Tulis batasan atau kondisi Anda di sini. (opsional)"
          value={answers.additional_constraints} onChange={(e) => set("additional_constraints", e.target.value)} />
      </QuestionBlock>
    </>
  );
}
